import React from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl-next';

import { FlagsProvider } from '@atlaskit/flag';
import { Inline, Text, Stack, Box } from '@atlaskit/primitives/compiled';
import { cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import QuestionCircleIcon from '@atlaskit/icon/core/question-circle';
import Tooltip from '@atlaskit/tooltip';

import type { AIEventsInstrumentationConfig } from '@atlassian/ai-analytics';
import { AIEventsInstrumentationProvider } from '@atlassian/ai-analytics';

import { ConfluenceLabsLozenge } from '@confluence/ai-common';
import {
	SmartsFeedbackComponent,
	FeedbackCollectorPropertiesProvider,
} from '@confluence/quick-summary';

import type { ActiveAudioContent } from '../../types/activeAudioContent';

const ANALYTICS_SOURCE = 'aiAudio';
const AUDIO_FEEDBACK_ENTRYPOINT_ID = 'ffb1f355-3d3c-45c7-a0d1-1f13eb13baa3';

const i18n = defineMessages({
	qualityDisclaimer: {
		id: 'audio.audio-briefing-popup.feedback-footer.quality-disclaimer',
		defaultMessage: 'Uses AI. Quality may vary.',
		description:
			'A disclaimer in the footer of our audio player warning that AI generated results may vary.',
	},
	aboutBriefingsLabel: {
		id: 'audio.audio-briefing-popup.feedback-footer.about-label',
		defaultMessage: 'What’s a briefing?',
		description:
			'Label prompting the user to learn more about briefings by hovering on it and activating a tooltip.',
	},
	aboutBriefingsTooltip: {
		id: 'audio.audio-briefing-popup.feedback-footer.about-tooltip',
		defaultMessage:
			'Briefings are personalized audio overviews that help you get the full picture of what you’re reading.',
		description: 'Tooltip text describing what briefings are.',
	},
});

const aiAnalyticsConfig: AIEventsInstrumentationConfig = {
	product: 'confluence',
	subproduct: 'confluence',
	source: 'page',
	aiFeatureName: 'aiAudio',
	proactiveGeneratedAI: 0,
	userGeneratedAI: 1,
};

export type AudioBriefingPopupFooterProps = {
	activeContent?: ActiveAudioContent;
};

const styles = cssMap({
	feedbackSection: {
		paddingBlock: token('space.100'),
		paddingInline: token('space.200'),
	},
	aboutSection: {
		backgroundColor: token('elevation.surface'),
		paddingBlock: token('space.100'),
		paddingInline: token('space.200'),
		// Top border
		borderTopWidth: token('border.width'),
		borderTopColor: token('color.border'),
		borderTopStyle: 'solid',
		// Bottom border
		borderBottomLeftRadius: token('border.radius.200'),
		borderBottomRightRadius: token('border.radius.200'),
		borderBottomWidth: token('border.width'),
		borderBottomColor: token('color.border'),
		borderBottomStyle: 'solid',
	},
});

export const AudioBriefingPopupFooterComponent = ({
	activeContent,
}: AudioBriefingPopupFooterProps) => {
	const { formatMessage } = useIntl();

	return (
		<Stack>
			<Box xcss={styles.aboutSection}>
				<Inline spread="space-between" alignBlock="center">
					<Tooltip content={formatMessage(i18n.aboutBriefingsTooltip)}>
						{(tooltipProps) => (
							<Box {...tooltipProps}>
								<Inline space="space.075" alignBlock="center">
									<QuestionCircleIcon
										label="questionIcon"
										size="small"
										spacing="none"
										color={token('color.icon.subtle')}
									/>
									<Text size="small" color="color.text.subtlest">
										<FormattedMessage {...i18n.aboutBriefingsLabel} />
									</Text>
								</Inline>
							</Box>
						)}
					</Tooltip>
					<ConfluenceLabsLozenge />
				</Inline>
			</Box>

			<Box xcss={styles.feedbackSection}>
				<Inline spread="space-between" alignBlock="center">
					<Text size="small" color="color.text.subtlest">
						<FormattedMessage {...i18n.qualityDisclaimer} />
					</Text>
					<SmartsFeedbackComponent
						contentId={activeContent?.id ?? ''}
						analyticsProperties={{
							source: ANALYTICS_SOURCE,
							additionalAnalyticsAttributes: {
								contentType: activeContent?.contentType ?? '',
							},
						}}
					/>
				</Inline>
			</Box>
		</Stack>
	);
};

export const AudioBriefingPopupFooter = ({ activeContent }: AudioBriefingPopupFooterProps) => {
	return (
		<FlagsProvider>
			<FeedbackCollectorPropertiesProvider entrypointId={AUDIO_FEEDBACK_ENTRYPOINT_ID}>
				<AIEventsInstrumentationProvider config={aiAnalyticsConfig}>
					<AudioBriefingPopupFooterComponent activeContent={activeContent} />
				</AIEventsInstrumentationProvider>
			</FeedbackCollectorPropertiesProvider>
		</FlagsProvider>
	);
};
